import { z } from "zod";
import { withPxUnit } from "@topol/utils";
import {
  TopolBlockCssClassSchema,
  TopolBlockSchema,
  TopolBlockAlignSchema,
  TopolVertcalAlignSchema,
  TopolColorSchema,
  TopolColorBearingSchema,
} from "../commonSchema";
import { TopolImageAndVideoBlockSchema } from "./imageAndVideoBlockSchema";
import { TopolButtonBlockSchema } from "./buttonBlockSchema";
import shortid from "shortid";
import { MergeTagsSchema } from "../../mergeTags/mergeTagsSchema";

const TopolLoopTextSchema = z.object({
  tagName: z.literal("mj-text"),
  uid: z.string().catch(() => shortid.generate()),
  attributes: z.object({
    containerWidth: z.number().catch(600),
    color: TopolColorSchema.catch("#000000"),
    // Templates saved before TPL-2470 carry a bare number here; normalize it
    // on load so the canvas and the store only ever see "<n>px".
    "font-size": z.preprocess(withPxUnit, z.string().catch("13px")),
    "font-family": z.string().catch("Helvetica, Arial, sans-serif"),
    "font-weight": z.string().optional(),
    "font-style": z.string().optional(),
    "text-decoration": z.string().optional(),
    "text-transform": z.string().optional(),
    "letter-spacing": z.string().optional(),
    "line-height": z.union([z.string(), z.number()]).optional(),
    align: TopolBlockAlignSchema.optional(),
    padding: z.string().optional(),
  }),
  content: z.string(),
  hidden: z.boolean().optional(),
});

const TopolLoopImageSchema = TopolImageAndVideoBlockSchema.extend({
  hidden: z.boolean().optional(),
});

const TopolLoopButtonSchema = TopolButtonBlockSchema.extend({
  hidden: z.boolean().optional(),
});

export const TopolLoopColumnSchema = z.object({
  tagName: z.literal("mj-column"),
  uid: z.string().catch(() => shortid.generate()),
  attributes: z
    .object({
      width: z.string().catch("100%"),
      "vertical-align": TopolVertcalAlignSchema.catch("top"),
      "border-right": TopolColorBearingSchema.optional().catch(undefined),
      "border-top": TopolColorBearingSchema.optional().catch(undefined),
      "border-left": TopolColorBearingSchema.optional().catch(undefined),
      "border-bottom": TopolColorBearingSchema.optional().catch(undefined),
      "background-color": TopolColorSchema.optional().catch(undefined),
      "column-gradient": TopolColorBearingSchema.optional().catch(undefined),
      "border-radius": z.string().optional().catch(undefined),
      border: TopolColorBearingSchema.optional().catch(undefined),
      padding: z.string().optional().catch(undefined),
      "css-class": z.string().nullable().optional().catch(undefined),
      "inner-attributes": z.boolean().optional().catch(undefined),
    })
    .catch({
      width: "100%",
      "vertical-align": "top",
    }),
  children: z
    .discriminatedUnion("tagName", [
      TopolLoopTextSchema,
      TopolLoopImageSchema,
      TopolLoopButtonSchema,
    ])
    .array()
    .catch([]),
});

export const TopolLoopBlockStyleAttributesSchema = z.object({
  padding: z.string().optional().catch(undefined),
  border: TopolColorBearingSchema.optional().catch(undefined),
  "border-right": TopolColorBearingSchema.optional().catch(undefined),
  "border-top": TopolColorBearingSchema.optional().catch(undefined),
  "border-left": TopolColorBearingSchema.optional().catch(undefined),
  "border-bottom": TopolColorBearingSchema.optional().catch(undefined),
  "border-radius": z.string().optional().catch(undefined),
  "background-color": TopolColorSchema.optional().catch(undefined),
  "in-group": z.boolean().optional().catch(true),
  gradient: TopolColorBearingSchema.optional().catch(undefined),
});

export const TopolLoopBlockSchema = TopolBlockSchema.extend({
  tagName: z.literal("mj-loop"),
  custom: z.literal(true).optional().catch(undefined),
  key: z.string().optional().catch(undefined),
  attributes: z
    .object({
      containerWidth: z.number().catch(600),
      "css-class": TopolBlockCssClassSchema.optional().catch(undefined),
      ...TopolLoopBlockStyleAttributesSchema.shape,
    })
    .catch({
      containerWidth: 600,
    }),
  layout: z.enum(["row", "column"]).catch("row"),
  maxPerRow: z.number().catch(3),
  children: z.array(TopolLoopColumnSchema),
  forMergeTag: MergeTagsSchema.nullable(),
});

export type TopolLoopBlock = z.infer<typeof TopolLoopBlockSchema>;
export type TopolLoopColumn = z.infer<typeof TopolLoopColumnSchema>;
export type TopolLoopText = z.infer<typeof TopolLoopTextSchema>;
export type TopolLoopImage = z.infer<typeof TopolLoopImageSchema>;
export type TopolLoopButton = z.infer<typeof TopolLoopButtonSchema>;
